import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useInfiniteQuery } from 'react-query';

import './ProductUpdates.scss';

import TableHeader from '../Table/TableHeader/TableHeader';
import useNotifications from '../../hooks/useNotifications';
import updateService from '../../services/updateService';
import ListUpdateSortColumn from '../../models/Update/ListUpdateSortColumn';
import GenericFormattedDate from '../GenericFormattedDate/GenericFormattedDate';
import updatesHelper from '../../helpers/updatesHelper';
import TitleTooltip from '../Tooltips/TitleTooltip/TitleTooltip';
import UpdatedAtSkeleton from '../Skeletons/UpdatedAtSkeleton';
import ProductUpdatesSkeleton from './ProductUpdatesSkeleton';
import useSortingViewState from '../../hooks/useSortingViewState';
import companiesHelper from '../../helpers/companiesHelper';
import productsHelper from '../../helpers/productsHelper';

const pageSize = 20;

const ProductUpdates: React.FC<Props> = ({ companyId, productId }) => {
  const { formatMessage: fm } = useIntl();
  const { notify } = useNotifications();
  const { getSortOrder } = useSortingViewState();
  const columns: { name: ListUpdateSortColumn | ''; key: string }[] = useMemo(() => {
    let productColumns: { name: ListUpdateSortColumn | ''; key: string }[] =
      !productId && !companyId ? [{ name: 'companyName', key: 'productUpdates.table.header.companyName' }] : [];
    productColumns = !productId
      ? [...productColumns, { name: 'productName', key: 'productUpdates.table.header.productName' }]
      : productColumns;

    return [
      ...productColumns,
      { name: 'typeName', key: 'productUpdates.table.header.typeName' },
      { name: 'createdAt', key: 'productUpdates.table.header.createdAt' },
      { name: '', key: '' }, // action
    ];
  }, [productId, companyId]);

  const {
    data: updatesData,
    isLoading: isUpdatesLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ['productUpdates', companyId, productId, getSortOrder<ListUpdateSortColumn>('createdAt', 'Updates', false)],
    async ({ pageParam = 1 }) => {
      const data = await updateService.list(
        companyId,
        productId,
        { pageSize, pageNumber: pageParam },
        getSortOrder<ListUpdateSortColumn>('createdAt', 'Updates', false).toString(),
      );
      return {
        ...data,
        nextPage: pageParam + 1,
      };
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.nextPage <= Math.ceil(allPages[0].total / pageSize)) {
          return lastPage.nextPage;
        }
        return undefined;
      },
      onError: () =>
        notify(
          'error',
          fm({ id: 'productUpdates.notification.title' }),
          fm({ id: 'productUpdates.notification.unableToLoadUpdates' }),
        ),
    },
  );

  return (
    <div className="product-updates-component mb-4 shadow-2">
      <div className="bg-color-gray-l3 p-4 rounded-top row">
        <div className="col text-uppercase font-bold">
          <FormattedMessage id="productUpdates.table.title" />
        </div>
        <div className="col-6 d-flex justify-content-end text-color-gray-l6 size-caption">
          {isUpdatesLoading && <UpdatedAtSkeleton />}
          {updatesData?.pages[0].updatedAt && (
            <span>
              <FormattedMessage id="productUpdates.updatedAt" />{' '}
              <GenericFormattedDate value={updatesData.pages[0].updatedAt} />
            </span>
          )}
        </div>
      </div>
      {updatesData?.pages?.[0]?.updates?.length === 0 && (
        <div className="row p-3 text-color-gray-l6">
          <div className="col d-flex justify-content-center">
            <FormattedMessage id="productUpdates.noData" />
          </div>
        </div>
      )}

      {isUpdatesLoading && <ProductUpdatesSkeleton cards={8} columns={columns} />}
      {!!updatesData?.pages?.[0]?.updates?.length && (
        <InfiniteScroll
          className="rounded-bottom"
          next={fetchNextPage}
          hasMore={hasNextPage || false}
          loader={null}
          dataLength={updatesData.pages.length}
          scrollableTarget="scrollContainer"
        >
          <table className="w-100 col-25-wide">
            <TableHeader<ListUpdateSortColumn> columns={columns} tableName="Updates" />
            <tbody className="animated-component-item">
              {updatesData.pages
                .flatMap((x) => x.updates)
                .map((update) => (
                  <tr key={update.id} data-test-id="updates-row">
                    {!companyId && !productId && (
                      <td data-test-id="updates-row-companyName" className="font-semi-bold">
                        <TitleTooltip
                          titleTooltip={update.companyName}
                          title={update.companyName}
                          titleClassName="inline-crop"
                          link={companiesHelper.getCompanyUrl(update.companyId)}
                        />
                      </td>
                    )}
                    {!productId && (
                      <td data-test-id="updates-row-productName" className="font-semi-bold">
                        <TitleTooltip
                          titleTooltip={update.productName}
                          title={update.productName}
                          titleClassName="inline-crop"
                          link={productsHelper.getProductUrl(update.productId)}
                        />
                      </td>
                    )}
                    <td data-test-id="updates-row-typeName">
                      <TitleTooltip
                        titleTooltip={fm({ id: `productUpdates.type.${update.typeName}.title` }, update.extendedData)}
                        title={fm({ id: `productUpdates.type.${update.typeName}.title` }, update.extendedData)}
                        titleClassName="inline-crop"
                        link={updatesHelper.getItemUrl(update)}
                      />
                    </td>
                    <td data-test-id="updates-row-createdAt">
                      <GenericFormattedDate value={update.createdAt} withTime />
                    </td>
                    <td align="right">
                      <Button
                        variant="secondary"
                        className="btn-toggle d-flex align-items-center justify-content-center"
                        href={updatesHelper.getItemUrl(update)}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </InfiniteScroll>
      )}
    </div>
  );
};

interface Props {
  companyId?: string;
  productId?: string;
}

export default ProductUpdates;
